import QRCode from "qrcode";

/**
 * Trigger browser download for a Blob
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Parse hex color to [r, g, b]
 */
const hexToRgb = (hex, defaultColor = [0, 0, 0]) => {
  if (!hex) return defaultColor;
  let clean = hex.replace("#", "").trim();
  if (clean.length === 3) {
    clean = clean.split("").map((c) => c + c).join("");
  }
  if (clean.length !== 6) return defaultColor;
  const num = parseInt(clean, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
};

/**
 * Encode Canvas ImageData into a 24-bit uncompressed BMP (Uint8Array)
 */
export const encodeBmp = (imageData) => {
  const { width, height, data } = imageData;
  const rowBytes = Math.floor((24 * width + 31) / 32) * 4;
  const pixelArraySize = rowBytes * height;
  const fileSize = 54 + pixelArraySize;

  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // BITMAPFILEHEADER (14 bytes)
  bytes[0] = 0x42; // 'B'
  bytes[1] = 0x4d; // 'M'
  view.setUint32(2, fileSize, true);
  view.setUint32(6, 0, true); // reserved
  view.setUint32(10, 54, true); // offset to pixel data

  // BITMAPINFOHEADER (40 bytes)
  view.setUint32(14, 40, true); // header size
  view.setInt32(18, width, true);
  view.setInt32(22, height, true); // positive = bottom-to-top
  view.setUint16(26, 1, true); // planes
  view.setUint16(28, 24, true); // 24 bpp
  view.setUint32(30, 0, true); // BI_RGB (uncompressed)
  view.setUint32(34, pixelArraySize, true);
  view.setInt32(38, 2835, true); // ~72 DPI
  view.setInt32(42, 2835, true);
  view.setUint32(46, 0, true);
  view.setUint32(50, 0, true);

  // Pixel rows bottom-to-top, BGR order
  const offset = 54;
  for (let y = height - 1; y >= 0; y--) {
    let rowOffset = offset + (height - 1 - y) * rowBytes;
    const srcRowStart = y * width * 4;
    for (let x = 0; x < width; x++) {
      const pxIndex = srcRowStart + x * 4;
      bytes[rowOffset++] = data[pxIndex + 2]; // B
      bytes[rowOffset++] = data[pxIndex + 1]; // G
      bytes[rowOffset++] = data[pxIndex]; // R
    }
  }

  return bytes;
};

/**
 * Encode Canvas ImageData into a single-frame GIF (Uint8Array) using LZW
 */
export const encodeGif = (imageData, fgHex = "#000000", bgHex = "#ffffff") => {
  const { width, height, data } = imageData;
  const fg = hexToRgb(fgHex, [0, 0, 0]);
  const bg = hexToRgb(bgHex, [255, 255, 255]);

  // 4-color palette table
  const palette = [
    bg[0], bg[1], bg[2], // index 0 = background
    fg[0], fg[1], fg[2], // index 1 = foreground
    bg[0], bg[1], bg[2],
    fg[0], fg[1], fg[2],
  ];

  const lzwMinCodeSize = 2;
  const clearCode = 1 << lzwMinCodeSize; // 4
  const eoiCode = clearCode + 1; // 5

  let codeSize = lzwMinCodeSize + 1;
  let nextCode = eoiCode + 1;
  const dictionary = new Map();

  const resetDict = () => {
    dictionary.clear();
    for (let i = 0; i < clearCode; i++) dictionary.set(i, i);
    nextCode = eoiCode + 1;
    codeSize = lzwMinCodeSize + 1;
  };
  resetDict();

  let bitBuffer = 0;
  let bitCount = 0;
  const outputBytes = [];

  const writeBits = (code, length) => {
    bitBuffer |= code << bitCount;
    bitCount += length;
    while (bitCount >= 8) {
      outputBytes.push(bitBuffer & 0xff);
      bitBuffer >>= 8;
      bitCount -= 8;
    }
  };

  writeBits(clearCode, codeSize);

  let curCode = null;
  const totalPixels = width * height;
  for (let i = 0; i < totalPixels; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    const a = data[i * 4 + 3];

    // If transparent or close to background luminance, assign index 0 (bg), else index 1 (fg)
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    const colorIdx = a < 128 || luminance > 127 ? 0 : 1;

    if (curCode === null) {
      curCode = colorIdx;
      continue;
    }

    const key = (curCode << 8) | colorIdx;
    if (dictionary.has(key)) {
      curCode = dictionary.get(key);
    } else {
      writeBits(curCode, codeSize);
      if (nextCode < 4096) {
        dictionary.set(key, nextCode++);
        if (nextCode > 1 << codeSize && codeSize < 12) {
          codeSize++;
        }
      } else {
        writeBits(clearCode, codeSize);
        resetDict();
      }
      curCode = colorIdx;
    }
  }

  if (curCode !== null) {
    writeBits(curCode, codeSize);
  }
  writeBits(eoiCode, codeSize);
  if (bitCount > 0) {
    outputBytes.push(bitBuffer & 0xff);
  }

  // Pack into sub-blocks (max 255 bytes each)
  const subBlocks = [];
  let pos = 0;
  while (pos < outputBytes.length) {
    const chunkSize = Math.min(255, outputBytes.length - pos);
    subBlocks.push(chunkSize);
    for (let i = 0; i < chunkSize; i++) {
      subBlocks.push(outputBytes[pos + i]);
    }
    pos += chunkSize;
  }
  subBlocks.push(0); // terminator

  const header = [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]; // "GIF89a"
  const screenDesc = [
    width & 0xff,
    (width >> 8) & 0xff,
    height & 0xff,
    (height >> 8) & 0xff,
    0x81, // 4-color global color table
    0,
    0,
  ];
  const imgDesc = [
    0x2c,
    0, 0, 0, 0,
    width & 0xff,
    (width >> 8) & 0xff,
    height & 0xff,
    (height >> 8) & 0xff,
    0x00,
  ];
  const trailer = [0x3b];

  return new Uint8Array([
    ...header,
    ...screenDesc,
    ...palette,
    ...imgDesc,
    lzwMinCodeSize,
    ...subBlocks,
    ...trailer,
  ]);
};

/**
 * Render QR code to an offscreen canvas and return it
 */
export const renderQrToCanvas = async ({
  text,
  size = 512,
  margin = 2,
  errorCorrectionLevel = "M",
  fgColor = "#000000",
  bgColor = "#ffffff",
}) => {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  // Fill background solid first
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, size, size);

  await QRCode.toCanvas(canvas, text, {
    width: size,
    margin,
    errorCorrectionLevel,
    color: {
      dark: fgColor,
      light: bgColor,
    },
  });

  return canvas;
};

/**
 * Generate a Blob for any supported format
 */
export const generateQrBlob = async ({
  text,
  format = "png",
  size = 512,
  quality = 0.92,
  errorCorrectionLevel = "M",
  fgColor = "#000000",
  bgColor = "#ffffff",
  margin = 2,
}) => {
  const fmt = format.toLowerCase();

  // SVG format
  if (fmt === "svg") {
    const svgString = await QRCode.toString(text, {
      type: "svg",
      width: size,
      margin,
      errorCorrectionLevel,
      color: {
        dark: fgColor,
        light: bgColor,
      },
    });
    return new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  }

  // Canvas-based formats
  const canvas = await renderQrToCanvas({
    text,
    size,
    margin,
    errorCorrectionLevel,
    fgColor,
    bgColor,
  });

  if (fmt === "png") {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to generate PNG blob"));
      }, "image/png");
    });
  }

  if (fmt === "jpg" || fmt === "jpeg") {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to generate JPG blob"));
        },
        "image/jpeg",
        quality
      );
    });
  }

  if (fmt === "webp") {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to generate WEBP blob"));
        },
        "image/webp",
        quality
      );
    });
  }

  if (fmt === "bmp") {
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, size, size);
    const bmpBytes = encodeBmp(imageData);
    return new Blob([bmpBytes], { type: "image/bmp" });
  }

  if (fmt === "gif") {
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, size, size);
    const gifBytes = encodeGif(imageData, fgColor, bgColor);
    return new Blob([gifBytes], { type: "image/gif" });
  }

  throw new Error(`Unsupported format: ${format}`);
};
